/**
 * Display formatting helpers.
 *
 * Centralized so any tweak (e.g., switch to "0.01 TON" vs "10 mTON")
 * propagates uniformly. Everything here is pure; safe to call in render.
 */

const TON_DECIMALS = 9n;
const NANO_PER_TON = 1_000_000_000n;

/**
 * Format a nano-TON amount string for display: "0.5 TON", "0.001 TON",
 * "12.345 TON". Trailing zeros are trimmed but we always keep at least one
 * digit after the decimal point for legibility (so "1 TON" -> "1.0 TON").
 *
 * Returns "?" if the input isn't a valid integer-as-string. We don't throw;
 * a malformed amount field is a server bug we want to surface visibly, not
 * crash the whole inbox over.
 */
export function formatTon(amountNano: string): string {
  if (!/^\d+$/.test(amountNano)) return "?";
  const n = BigInt(amountNano);
  const whole = n / NANO_PER_TON;
  const frac = n % NANO_PER_TON;
  if (frac === 0n) return `${whole.toString()}.0 TON`;
  // Pad to 9 digits, then strip trailing zeros.
  const fracStr = frac.toString().padStart(Number(TON_DECIMALS), "0").replace(/0+$/, "");
  return `${whole.toString()}.${fracStr.length > 0 ? fracStr : "0"} TON`;
}

/**
 * Parse user input ("0.5", "1", "0.001") into nano-TON string.
 * Returns null on malformed input. Caps the fractional part at 9 digits.
 */
export function parseTonInput(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  const m = trimmed.match(/^(\d+)(?:\.(\d{1,9}))?$/);
  if (!m) return null;
  const whole = BigInt(m[1] ?? "0");
  const fracDigits = (m[2] ?? "").padEnd(9, "0");
  const frac = BigInt(fracDigits);
  return (whole * NANO_PER_TON + frac).toString();
}

/**
 * Truncate a TON address for inline display: `UQABcd…wxyz`.
 * Works on both `0:hex` raw and base64url user-friendly forms.
 */
export function shortenAddress(addr: string, head = 4, tail = 4): string {
  if (addr.length <= head + tail + 1) return addr;
  if (addr.startsWith("0:")) {
    // `0:abcd...wxyz` — keep the workchain prefix
    return `0:${addr.slice(2, 2 + head)}…${addr.slice(-tail)}`;
  }
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

/**
 * Relative time, chat-app style:
 *   < 60s: "now"
 *   < 60m: "12m"
 *   < 24h: "3h"
 *   < 7d:  "2d"
 *   else:  locale short date "May 21"
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const diff = now.getTime() - then.getTime();
  if (diff < 0) return "now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Absolute time for inside-bubble timestamps. Today: "14:22", earlier this
 * year: "May 21", older: "May 21, 2025".
 */
export function bubbleTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const sameDay =
    then.getFullYear() === now.getFullYear() &&
    then.getMonth() === now.getMonth() &&
    then.getDate() === now.getDate();
  if (sameDay) {
    return then.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  const sameYear = then.getFullYear() === now.getFullYear();
  if (sameYear) {
    return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return then.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Hard cap for the inbox row preview line — 1 line of ~60 chars. */
export function previewClip(text: string, max = 80): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}
